import { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import * as axios from "axios";

import { Videos } from "../routes/Videos";
import { ContentBlockHeader } from "./ContentBlockHeader";
import { Header } from "./Header";
import { VideosToolbar } from "./VideosToolbar";

import { FolderMetadata } from "vimeo";

import { Data } from "blocksdk";

import { isContentBlockContext } from "../sessionStorage";
import {
    EventName,
    getSfmcLocation,
    sendAnalyticsEvent,
} from "../analytics/common";
import { refreshSfmcToken, getSfmcAuthorizeUrl } from "../sfmcClient";

export function Home() {
    const [selectedFolder, setSelectedFolder] = useState<string>("");
    const [searchInput, setSearchInput] = useState<string>("");
    const [numRootFolderVideos, setNumRootFolderVideos] = useState(0);
    const [sfmcAuthComplete, setSfmcAuthComplete] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        refreshSfmcToken()
            .then((resp: axios.AxiosResponse) => {
                // Something's not quite right here that requires us to
                // check if the response is undefined. When the call fails
                // with a 401, for whatever reason, `refreshSfmcToken` isn't
                // throwing an error as usual. So this sort of a check is
                // necessary.
                // Don't use optional chaining here since it changes the meaning
                // of this if-condition.
                if (!resp || resp.status === 401) {
                    window.location.href = getSfmcAuthorizeUrl();
                    return;
                }
                setSfmcAuthComplete(true);
            })
            .catch((err: any) => {
                const axiosErr = err as axios.AxiosError;
                if (axiosErr.response?.status === 401) {
                    window.location.href = getSfmcAuthorizeUrl();
                    return;
                }
            });
    }, []);

    function onFolderSelected(uri: string, _folderMetadata?: FolderMetadata) {
        setSelectedFolder(uri);
    }

    const contentBlockContext = isContentBlockContext();

    // Important: Don't move this below the code where we
    // fetch the data from the content block.
    // We need to send the app view event before we check for
    // the content block data since we might automatically
    // navigate the user to the settings page in a web content
    // block context below.
    useEffect(() => {
        sendAnalyticsEvent(EventName.pageView, {
            name: "vimeo_app",
            location: getSfmcLocation(),
            video_id: null,
            section: null,
        }, 2);
    }, []);

    // If the app is loaded inside a content block,
    // automatically navigate to the previously
    // configured video, if there is one.
    if (contentBlockContext) {
        window.blockSdk.getData((data: Data) => {
            if (data.web) {
                navigate(`${data.web.videoUri}/settings/embed-for-web`);
            }
        });
    }

    return !sfmcAuthComplete ? (
        <></>
    ) : (
        <div className="App">
            {/*
                In a content block context, the app header will also contain the folder navigation.
                It's easy if we just show an entirely different header component for that.
             */}
            {!contentBlockContext ? (
                <Header />
            ) : (
                <ContentBlockHeader
                    onFolderSelected={onFolderSelected}
                    onSearchChange={(e, v) => setSearchInput(v.value)}
                    numRootFolderVideos={numRootFolderVideos}
                />
            )}
            <main
                className="slds-var-p-around_large"
                style={{ marginTop: "50px" }}
            >
                <section className="slds-var-p-around_medium">
                    {!contentBlockContext && (
                        <VideosToolbar
                            onFolderSelected={onFolderSelected}
                            onSearchChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                                setSearchInput(e.target.value);
                            }}
                            numRootFolderVideos={numRootFolderVideos}
                        />
                    )}
                    <Videos
                        selectedFolder={selectedFolder}
                        searchInput={searchInput}
                        onSetNumRootFolderVideos={(count) =>
                            setNumRootFolderVideos(count)
                        }
                    />
                </section>
            </main>
        </div>
    );
}
